import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, User, ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { getBlogs } from "../services/services";

const IMAGE = "https://miniapptech.in/assets/img/blog";
const VIDEO = "https://miniapptech.in/assets/img/blog_video";
const BlogPage = () => {
  const [allPosts, setAllPosts] = useState([]);
  const [visiblePosts, setVisiblePosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const LIMIT = 6;

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);

    const res = await getBlogs();

    if (res.status) {
      setAllPosts(res.data);
      setVisiblePosts(res.data.slice(0, LIMIT));

      if (res.data.length <= LIMIT) {
        setHasMore(false);
      }
    }

    setLoading(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + document.documentElement.scrollTop + 100 >=
        document.documentElement.offsetHeight
      ) {
        // 🔥 move logic here instead of calling loadMore
        if (loading || !hasMore) return;

        setLoading(true);

        setTimeout(() => {
          const next = visiblePosts.length + LIMIT;

          setVisiblePosts(allPosts.slice(0, next));

          if (next >= allPosts.length) {
            setHasMore(false);
          }

          setLoading(false);
        }, 800);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [visiblePosts, hasMore, loading, allPosts]);

  const getEmbedUrl = (url) => {
    if (!url) return "";

    if (url.includes("youtu.be")) {
      const id = url.split("/").pop().split("?")[0];
      return `https://www.youtube.com/embed/${id}`;
    }

    if (url.includes("youtube.com")) {
      const id = new URL(url).searchParams.get("v");
      return `https://www.youtube.com/embed/${id}`;
    }

    return url;
  };

  const isYouTube = (url) =>
    url?.includes("youtube.com") || url?.includes("youtu.be");

  if (!allPosts.length && loading) {
    return <p className="text-center mt-20">Loading...</p>;
  }
  return (
    <div className="pt-24 pb-20 bg-light min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-12 gap-6">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Link
              to="/"
              className="inline-flex items-center text-primary font-bold hover:gap-2 transition-all mb-4 group"
            >
              <ArrowLeft
                size={18}
                className="mr-2 transition-transform group-hover:-translate-x-1"
              />{" "}
              Back to Home
            </Link>
            <h1 className="text-4xl lg:text-5xl font-bold text-dark">
              Latest <span className="text-primary">Insights</span>
            </h1>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="text-gray-600 max-w-md md:text-right"
          >
            A deep dive into the world of technology, design, and business
            innovation.
          </motion.p>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence>
            {visiblePosts.map((post, index) => (
              <motion.div
                key={post.blog_detail_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (index % 3) * 0.1 }}
                whileHover={{ y: -10 }}
                className="bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-soft-lg transition-all duration-300 group"
              >
                <div className="relative h-48 overflow-hidden bg-black flex items-center justify-center">
                  {(() => {
                    const hasVideo = post.video && post.video.trim() !== "";
                    const hasImages =
                      Array.isArray(post.image) && post.image.length > 0;

                    if (hasVideo) {
                      return isYouTube(post.video) ? (
                        <iframe
                          src={getEmbedUrl(post.video)}
                          className="w-full h-full"
                          allowFullScreen
                          title="video"
                        />
                      ) : (
                        <video
                          className="w-full h-full object-cover object-center"
                          muted
                          autoPlay
                          loop
                        >
                          <source
                            src={`${VIDEO}/${post.video}`}
                            type="video/mp4"
                          />
                        </video>
                      );
                    }

                    if (hasImages) {
                      return (
                        <img
                          src={`${IMAGE}/${post.image[0]}`}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                        />
                      );
                    }

                    return (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        No Media
                      </div>
                    );
                  })()}

                  <div className="absolute top-4 left-4 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                    {post.category}
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center text-sm text-gray-500 mb-4 gap-4">
                    <span className="flex items-center gap-1">
                      <Calendar size={14} /> {post.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <User size={14} /> {post.author}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-dark mb-3 group-hover:text-primary transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed mb-4 line-clamp-2">
                    {post.excerpt}
                  </p>

                  <Link
                    to={`/blog/${post.blog_detail_id}`}
                    className="inline-flex items-center gap-2 text-primary font-bold hover:gap-3 transition-all"
                  >
                    Read More <ArrowRight size={16} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Loading Indicator */}
        {hasMore && (
          <div className="mt-12 flex justify-center">
            {loading ? (
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="animate-spin text-primary" size={32} />
                <p className="text-gray-500 font-medium">
                  Loading more stories...
                </p>
              </div>
            ) : (
              <p className="text-gray-400 italic font-medium">
                Scroll down for more
              </p>
            )}
          </div>
        )}

        {!hasMore && (
          <div className="mt-12 text-center text-gray-400 border-t border-gray-200 pt-8">
            <p className="font-medium">
              You've reached the end of our current insights. Stay tuned for
              more!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogPage;
